import { Controller } from 'react-hook-form';
import Select from 'react-select';
import Input from '../../../../components/forms/input.component';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import Title from '../../../../components/forms/titles.component';
import { validateVoiceSpec } from '../../../../services/cpd.service';
import type { CpdOverrides } from '../../../../services/cpd.service';

/**
 * Sección de "overrides" (parámetros forzados) compartida por Operators, Routes y
 * Route-operators. Todos los campos son opcionales: vacío = heredar de la capa
 * anterior (default → template → route → operator → route_operator).
 *
 * En el form los valores viven como strings ('' = heredar) y `toApiOverrides`
 * los convierte al shape que espera `overridesSchema` (.strict()).
 */

export const INHERIT = '';

export const MODE_OPTIONS = [
  { value: INHERIT, label: '— Heredar —' },
  { value: 'energy', label: 'Energy' },
  { value: 'tone', label: 'Tone Detect' },
  { value: 'dtmf', label: 'DTMF' },
  { value: 'ml', label: 'ML' },
  { value: 'voice', label: 'Voice / AMD (post-answer)' }
];

export const ACTION_OPTIONS = [
  { value: INHERIT, label: '— Heredar —' },
  { value: 'hangup_603', label: 'Colgar con 603 Decline' },
  { value: 'continue', label: 'Continuar la llamada' },
  { value: 'transfer', label: 'Transferir' }
];

export const BYPASS_OPTIONS = [
  { value: INHERIT, label: '— Heredar —' },
  { value: 'true', label: 'Sí (sin CPD)' },
  { value: 'false', label: 'No (con CPD)' }
];

export const CARRIER_OPTIONS = [
  { value: INHERIT, label: '— Heredar —' },
  { value: 'TELCEL', label: 'TELCEL' },
  { value: 'ATT', label: 'AT&T' },
  { value: 'MOVISTAR', label: 'MOVISTAR' },
  { value: 'ALTAN', label: 'ALTÁN' }
];

export interface OverridesFormValues {
  mode: string;
  action: string;
  transferTo: string;
  toneSpec: string;
  toneCarrier: string;
  toneStartDelayMs: string;
  voiceSpec: string;
  bypass: string;
  th: {
    maxAnalysisMs: string;
    minEnergyDbfs: string;
    minVoiceMs: string;
    maxSilenceMs: string;
    dtmfHumanDigits: string;
    mlConfidenceMin: string;
  };
}

export const EMPTY_OVERRIDES: OverridesFormValues = {
  mode: INHERIT,
  action: INHERIT,
  transferTo: '',
  toneSpec: '',
  toneCarrier: INHERIT,
  toneStartDelayMs: '',
  voiceSpec: '',
  bypass: INHERIT,
  th: {
    maxAnalysisMs: '',
    minEnergyDbfs: '',
    minVoiceMs: '',
    maxSilenceMs: '',
    dtmfHumanDigits: '',
    mlConfidenceMin: ''
  }
};

const str = (v: unknown): string => (v === undefined || v === null ? '' : String(v));

/** API → form (para editar). */
export function fromApiOverrides(ov?: CpdOverrides | null): OverridesFormValues {
  const o = ov ?? {};
  const th = o.thresholds ?? {};
  return {
    mode: str(o.mode),
    action: str(o.action),
    transferTo: str(o.transferTo),
    toneSpec: str(o.toneSpec),
    toneCarrier: str(o.toneCarrier),
    toneStartDelayMs: str(o.toneStartDelayMs),
    voiceSpec: str(o.voiceSpec),
    bypass: o.bypass === undefined ? INHERIT : String(o.bypass),
    th: {
      maxAnalysisMs: str(th.maxAnalysisMs),
      minEnergyDbfs: str(th.minEnergyDbfs),
      minVoiceMs: str(th.minVoiceMs),
      maxSilenceMs: str(th.maxSilenceMs),
      dtmfHumanDigits: str(th.dtmfHumanDigits),
      mlConfidenceMin: str(th.mlConfidenceMin)
    }
  };
}

const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));

/** form → API. Omite todo lo vacío para respetar `.strict()` y la semántica "heredar". */
export function toApiOverrides(f: OverridesFormValues): CpdOverrides {
  const out: CpdOverrides = {};
  if (f.mode) out.mode = f.mode as CpdOverrides['mode'];
  if (f.action) out.action = f.action as CpdOverrides['action'];
  if (f.transferTo.trim()) out.transferTo = f.transferTo.trim();
  if (f.toneSpec.trim()) out.toneSpec = f.toneSpec.trim();
  if (f.toneCarrier) out.toneCarrier = f.toneCarrier;
  const delay = num(f.toneStartDelayMs);
  if (delay !== undefined) out.toneStartDelayMs = delay;
  if (f.voiceSpec.trim()) out.voiceSpec = f.voiceSpec.trim();
  if (f.bypass) out.bypass = f.bypass === 'true';

  const th: NonNullable<CpdOverrides['thresholds']> = {};
  const maxAnalysisMs = num(f.th.maxAnalysisMs);
  const minEnergyDbfs = num(f.th.minEnergyDbfs);
  const minVoiceMs = num(f.th.minVoiceMs);
  const maxSilenceMs = num(f.th.maxSilenceMs);
  const mlConfidenceMin = num(f.th.mlConfidenceMin);
  if (maxAnalysisMs !== undefined) th.maxAnalysisMs = maxAnalysisMs;
  if (minEnergyDbfs !== undefined) th.minEnergyDbfs = minEnergyDbfs;
  if (minVoiceMs !== undefined) th.minVoiceMs = minVoiceMs;
  if (maxSilenceMs !== undefined) th.maxSilenceMs = maxSilenceMs;
  if (f.th.dtmfHumanDigits.trim()) th.dtmfHumanDigits = f.th.dtmfHumanDigits.trim();
  if (mlConfidenceMin !== undefined) th.mlConfidenceMin = mlConfidenceMin;
  if (Object.keys(th).length > 0) out.thresholds = th;

  return out;
}

export function countOverrides(f: OverridesFormValues): number {
  const o = toApiOverrides(f);
  const n = Object.keys(o).filter((k) => k !== 'thresholds').length;
  return n + Object.keys(o.thresholds ?? {}).length;
}

interface Props {
  control: any;
  register: any;
  errors: any;
  watch: any;
  /** prefijo del campo en el form, por defecto 'ov' */
  prefix?: string;
}

const optionalNumber = (min: number, max: number, label: string) => ({
  validate: (v: string) => {
    if (v === undefined || v === null || String(v).trim() === '') return true;
    const n = Number(v);
    if (!Number.isInteger(n)) return `${label} debe ser entero`;
    if (n < min || n > max) return `${label} debe estar entre ${min} y ${max}`;
    return true;
  }
});

export default function CpdOverridesFields({
  control,
  register,
  errors,
  watch,
  prefix = 'ov'
}: Props) {
  const p = (name: string) => `${prefix}.${name}`;
  const e = (path: string) => path.split('.').reduce((acc: any, k) => acc?.[k], errors);
  const action = watch(p('action'));
  const mode = watch(p('mode'));

  const renderSelect = (
    name: string,
    options: { value: string; label: string }[],
    label: string
  ) => (
    <div className="fv-row">
      <label className="form-label">{label}</label>
      <Controller
        control={control}
        name={p(name)}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Select
              options={options}
              isClearable={false}
              className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
              classNamePrefix="form-select-custom"
              onChange={(opt: any) => onChange(opt ? opt.value : INHERIT)}
              value={options.find((o) => o.value === (value ?? INHERIT)) ?? options[0]}
            />
            <InvalidFeedback error={error} />
          </>
        )}
      />
    </div>
  );

  return (
    <div className="bg-light-nested rounded-4 p-6 border border-dashed border-gray-300">
      <Title
        title="Parámetros forzados (overrides)"
        subtitle="Vacío = heredar del template / capa anterior"
        icon="ki-setting-2"
        type="warning"
      />

      <div className="row g-4">
        <div className="col-sm-6">{renderSelect('mode', MODE_OPTIONS, 'Modo')}</div>
        <div className="col-sm-6">
          {renderSelect('action', ACTION_OPTIONS, 'Acción al detectar buzón')}
        </div>

        {action === 'transfer' && (
          <div className="col-sm-12">
            <Input
              label="Transferir a"
              requiredIndicator="required"
              placeholder="Extensión o número"
              errors={e(p('transferTo'))}
              {...register(p('transferTo'), {
                required: 'action=transfer requiere transferTo',
                maxLength: { value: 64, message: 'Máximo 64 caracteres' }
              })}
            />
          </div>
        )}

        <div className="col-sm-6">{renderSelect('bypass', BYPASS_OPTIONS, 'Bypass (sin CPD)')}</div>
        <div className="col-sm-6">
          {renderSelect('toneCarrier', CARRIER_OPTIONS, 'Carrier del tono')}
        </div>

        <div className="col-sm-12">
          <Input
            label="ToneSpec (patrón tone_detect)"
            placeholder="vmtelcel 404 r +1000 hangup 21 1"
            errors={e(p('toneSpec'))}
            {...register(p('toneSpec'), {
              pattern: {
                value: /^[A-Za-z0-9_,.\-+=\s]{1,255}$/,
                message: 'Solo letras, números, espacios y _ , . - + ='
              }
            })}
          />
        </div>

        <div className="col-sm-6">
          <Input
            label="Delay antes de tone_detect (ms)"
            type="number"
            placeholder="Ej. 25000 (MOVISTAR)"
            errors={e(p('toneStartDelayMs'))}
            {...register(p('toneStartDelayMs'), optionalNumber(0, 300000, 'toneStartDelayMs'))}
          />
        </div>

        <div className="col-sm-12">
          <Input
            label="VoiceSpec (parametros de mod_amd, post-answer)"
            placeholder="total_analysis_time=6000,after_greeting_silence=800,greeting=2500"
            errors={e(p('voiceSpec'))}
            {...register(p('voiceSpec'), {
              validate: (v: string) => {
                const val = String(v ?? '').trim();
                if (val === '') return true;
                return validateVoiceSpec(val) ?? true;
              }
            })}
          />
          <div className="form-text">
            Solo aplica en modo <span className="fw-semibold">Voice / AMD</span>. Pares{' '}
            <code>clave=entero</code> separados por comas. Sin voiceSpec, un nodo sin{' '}
            <code>amd.conf.xml</code> corre mod_amd con todos los umbrales en cero.
          </div>
        </div>

        {(mode === 'energy' || mode === INHERIT) && (
          <>
            <div className="col-sm-6">
              <Input
                label="Max Analysis (ms)"
                type="number"
                placeholder="500 – 60000"
                errors={e(p('th.maxAnalysisMs'))}
                {...register(p('th.maxAnalysisMs'), optionalNumber(500, 60000, 'maxAnalysisMs'))}
              />
            </div>
            <div className="col-sm-6">
              <Input
                label="Min Energy (dBFS)"
                type="number"
                placeholder="-80 – 0"
                errors={e(p('th.minEnergyDbfs'))}
                {...register(p('th.minEnergyDbfs'), optionalNumber(-80, 0, 'minEnergyDbfs'))}
              />
            </div>
            <div className="col-sm-6">
              <Input
                label="Min Voice (ms)"
                type="number"
                placeholder="100 – 20000"
                errors={e(p('th.minVoiceMs'))}
                {...register(p('th.minVoiceMs'), optionalNumber(100, 20000, 'minVoiceMs'))}
              />
            </div>
            <div className="col-sm-6">
              <Input
                label="Max Silence (ms)"
                type="number"
                placeholder="100 – 20000"
                errors={e(p('th.maxSilenceMs'))}
                {...register(p('th.maxSilenceMs'), optionalNumber(100, 20000, 'maxSilenceMs'))}
              />
            </div>
          </>
        )}

        {mode === 'dtmf' && (
          <div className="col-sm-6">
            <Input
              label="DTMF dígitos humano"
              placeholder="0-9 * # (máx. 8)"
              errors={e(p('th.dtmfHumanDigits'))}
              {...register(p('th.dtmfHumanDigits'), {
                pattern: { value: /^[0-9*#]{0,8}$/, message: '0..8 caracteres de 0-9, *, #' }
              })}
            />
          </div>
        )}

        {mode === 'ml' && (
          <div className="col-sm-6">
            <Input
              label="ML confidence min (0 – 1)"
              type="number"
              step="any"
              placeholder="Ej. 0.8"
              errors={e(p('th.mlConfidenceMin'))}
              {...register(p('th.mlConfidenceMin'), {
                validate: (v: string) => {
                  if (v === undefined || String(v).trim() === '') return true;
                  const n = Number(v);
                  return (n >= 0 && n <= 1) || 'Debe estar entre 0 y 1';
                }
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
